import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  buildGoogleFontsUrl,
  getFontFamily,
  getTextDirection,
  getFontConfig,
  isRTL,
} from "@/config/fonts";

// Track loaded fonts to avoid duplicate requests
const loadedFonts = new Set();

// Font link element ID
const FONT_LINK_ID = "dynamic-noto-font";

/**
 * Load a font from Google Fonts
 * @param {string} lang - Language code
 * @returns {Promise<void>}
 */
const loadFont = (lang) => {
  const config = getFontConfig(lang);
  const fontKey = config.family;

  // Skip if already loaded
  if (loadedFonts.has(fontKey)) {
    return;
  }

  const url = buildGoogleFontsUrl(lang);

  return new Promise((resolve, reject) => {
    // Check if link already exists
    const existingLink = document.getElementById(FONT_LINK_ID);

    // Create new link element
    const link = document.createElement("link");
    link.id = FONT_LINK_ID;
    link.rel = "stylesheet";
    link.href = url;
    link.crossOrigin = "anonymous";

    link.onload = () => {
      loadedFonts.add(fontKey);
      resolve();
    };

    link.onerror = () => {
      console.warn(`Failed to load font for language: ${lang}`);
      reject(new Error(`Failed to load font: ${fontKey}`));
    };

    // Remove existing and add new
    if (existingLink) {
      existingLink.remove();
    }
    document.head.appendChild(link);
  });
};

/**
 * Apply font styles to the document
 * @param {string} lang - Language code
 */
const applyFontStyles = (lang) => {
  const fontFamily = getFontFamily(lang);
  const direction = getTextDirection(lang);

  // Update CSS custom property
  document.documentElement.style.setProperty("--font-family-noto", fontFamily);

  // Update body font-family
  document.body.style.fontFamily = fontFamily;

  // Update text direction
  document.documentElement.dir = direction;
  document.documentElement.lang = lang;

  // Add/remove RTL class for additional styling hooks
  if (isRTL(lang)) {
    document.documentElement.classList.add("rtl");
    document.documentElement.classList.remove("ltr");
  } else {
    document.documentElement.classList.add("ltr");
    document.documentElement.classList.remove("rtl");
  }
};

/**
 * Hook for dynamic Noto font loading based on current language
 *
 * @example
 * function App() {
 *   const { isLoading, error, fontFamily, direction } = useDynamicFont();
 *
 *   if (isLoading) return <LoadingSpinner />;
 *
 *   return <div style={{ fontFamily }}>{children}</div>;
 * }
 */
export const useDynamicFont = () => {
  const { i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fontFamily, setFontFamily] = useState(getFontFamily("en"));
  const [direction, setDirection] = useState("ltr");

  // Load font for a specific language
  const loadFontForLanguage = useCallback(async (lang) => {
    setIsLoading(true);
    setError(null);

    try {
      await loadFont(lang);
      applyFontStyles(lang);
      setFontFamily(getFontFamily(lang));
      setDirection(getTextDirection(lang));
    } catch (err) {
      console.error("Font loading error:", err);
      setError(err);
      // Still apply styles with fallback fonts
      applyFontStyles(lang);
      setFontFamily(getFontFamily(lang));
      setDirection(getTextDirection(lang));
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load font on mount and language change
  useEffect(() => {
    // Load font for current language
    loadFontForLanguage(i18n.language);

    // Listen for language changes
    const handleLanguageChange = (lang) => {
      loadFontForLanguage(lang);
    };

    i18n.on("languageChanged", handleLanguageChange);

    return () => {
      i18n.off("languageChanged", handleLanguageChange);
    };
  }, [i18n, loadFontForLanguage]);

  return {
    isLoading,
    error,
    fontFamily,
    direction,
    isRTL: direction === "rtl",
    currentLanguage: i18n.language,
    loadFont: loadFontForLanguage,
  };
};

/**
 * Preload fonts for specific languages
 * Useful for preloading fonts you know will be needed
 *
 * @param {string[]} languages - Array of language codes to preload
 */
export const preloadFonts = (languages) => {
  const promises = languages.map((lang) => {
    const config = getFontConfig(lang);
    if (!loadedFonts.has(config.family)) {
      const url = buildGoogleFontsUrl(lang);
      const link = document.createElement("link");
      link.rel = "preload";
      link.as = "style";
      link.href = url;
      document.head.appendChild(link);

      return loadFont(lang);
    }
    return Promise.resolve();
  });

  return Promise.all(promises);
};

/**
 * Get list of currently loaded fonts
 */
export const getLoadedFonts = () => Array.from(loadedFonts);

export default useDynamicFont;
